import {
    Box, FormControl, FormLabel, Input, Select, Stack, Switch, Wrap, WrapItem,
} from "@chakra-ui/react";
import { MEMBER_ROLE_OPTIONS } from "../groupsConstants";
import { SectionTitle } from "../groupsUi";

/**
 * Permission switches displayed in the form.
 */
const PERMISSION_FIELDS = [
    { key: "can_manage_bookings", label: "Gérer les réservations" },
    { key: "can_edit_profile", label: "Modifier le profil" },
    { key: "can_edit_service", label: "Modifier les services" },
    { key: "can_send_message", label: "Envoyer des messages" },
];

/**
 * Builds the empty form state for a new member.
 * @returns {Object}
 */
export function buildEmptyMemberForm() {
    return {
        surname: "", email: "", role: "", payout_percent: "",
        is_leader: false,
        can_manage_bookings: false, can_edit_profile: false,
        can_edit_service: false, can_send_message: false,
    };
}

/**
 * Builds the form state from an existing member entity.
 * @param {Object} member - Existing member
 * @returns {Object}
 */
export function buildMemberFormFrom(member) {
    return {
        surname: member?.surname || "",
        email: member?.email || "",
        role: member?.role || "",
        payout_percent: member?.payout_percent ?? "",
        is_leader: Boolean(member?.is_leader),
        can_manage_bookings: Boolean(member?.can_manage_bookings),
        can_edit_profile: Boolean(member?.can_edit_profile),
        can_edit_service: Boolean(member?.can_edit_service),
        can_send_message: Boolean(member?.can_send_message),
    };
}

/**
 * Validates the form for create vs update.
 * @param {Object} form - Form state
 * @param {boolean} isCreate - True for the add flow
 * @returns {string|null}
 */
export function validateMemberForm(form, isCreate) {
    if (isCreate && !form.surname?.trim()) return "Le nom du membre est requis";
    if (isCreate && !form.email?.trim()) return "L'email du membre est requis";
    if (form.payout_percent !== "" && form.payout_percent !== null) {
        const n = Number(form.payout_percent);
        if (Number.isNaN(n) || n < 0 || n > 100) return "Le pourcentage doit être entre 0 et 100";
    }
    return null;
}

/**
 * Builds the API payload from the form state.
 * @param {Object} form - Form state
 * @returns {Object}
 */
export function buildMemberPayload(form) {
    const payload = {
        is_leader: Boolean(form.is_leader),
        can_manage_bookings: Boolean(form.can_manage_bookings),
        can_edit_profile: Boolean(form.can_edit_profile),
        can_edit_service: Boolean(form.can_edit_service),
        can_send_message: Boolean(form.can_send_message),
    };
    if (form.surname?.trim()) payload.surname = form.surname.trim();
    if (form.email?.trim()) payload.email = form.email.trim();
    if (form.role?.trim()) payload.role = form.role.trim();
    if (form.payout_percent !== "" && form.payout_percent !== null) {
        payload.payout_percent = Number(form.payout_percent);
    }
    return payload;
}

/**
 * Identity row (surname + email).
 * @param {Object} props - { form, onChange, isCreate }
 */
function IdentityRow({ form, onChange, isCreate }) {
    return (
        <Stack direction={{ base: "column", sm: "row" }} spacing="12px">
            <FormControl isRequired={isCreate}>
                <FormLabel fontSize="0.8rem">Nom du membre</FormLabel>
                <Input size="sm" borderRadius="8px" value={form.surname} onChange={(e) => onChange("surname", e.target.value)} />
            </FormControl>
            <FormControl isRequired={isCreate}>
                <FormLabel fontSize="0.8rem">Email</FormLabel>
                <Input size="sm" borderRadius="8px" type="email" value={form.email} isDisabled={!isCreate}
                    onChange={(e) => onChange("email", e.target.value)} />
            </FormControl>
        </Stack>
    );
}

/**
 * Role + payout percent row.
 * @param {Object} props - { form, onChange }
 */
function RolePayoutRow({ form, onChange }) {
    return (
        <Stack direction={{ base: "column", sm: "row" }} spacing="12px">
            <FormControl>
                <FormLabel fontSize="0.8rem">Rôle</FormLabel>
                <Select size="sm" borderRadius="8px" placeholder="—" value={form.role} onChange={(e) => onChange("role", e.target.value)}>
                    {MEMBER_ROLE_OPTIONS.map((r) => (<option key={r} value={r}>{r}</option>))}
                </Select>
            </FormControl>
            <FormControl>
                <FormLabel fontSize="0.8rem">Payout (%)</FormLabel>
                <Input size="sm" borderRadius="8px" type="number" min={0} max={100} step={0.1}
                    value={form.payout_percent} onChange={(e) => onChange("payout_percent", e.target.value)} />
            </FormControl>
        </Stack>
    );
}

/**
 * Single permission toggle (switch + label).
 * @param {Object} props - { label, isChecked, onChange }
 */
function PermissionSwitch({ label, isChecked, onChange }) {
    return (
        <FormControl display="flex" alignItems="center" w="auto" gap="8px">
            <Switch colorScheme="brand" isChecked={isChecked} onChange={onChange} />
            <FormLabel mb="0" fontSize="0.8rem">{label}</FormLabel>
        </FormControl>
    );
}

/**
 * Permission switches block (4 toggles + leader).
 * @param {Object} props - { form, onChange }
 */
function PermissionsBlock({ form, onChange }) {
    return (
        <Box>
            <SectionTitle>Permissions</SectionTitle>
            <Wrap spacing="12px">
                <WrapItem>
                    <PermissionSwitch label="Leader" isChecked={form.is_leader} onChange={(e) => onChange("is_leader", e.target.checked)} />
                </WrapItem>
                {PERMISSION_FIELDS.map((p) => (
                    <WrapItem key={p.key}>
                        <PermissionSwitch label={p.label} isChecked={Boolean(form[p.key])} onChange={(e) => onChange(p.key, e.target.checked)} />
                    </WrapItem>
                ))}
            </Wrap>
        </Box>
    );
}

/**
 * Reusable member form fields layout (used by Add and Edit dialogs).
 * @param {Object} props - { form, onChange, isCreate }
 */
function MemberFormFields({ form, onChange, isCreate = false }) {
    return (
        <Stack spacing="16px">
            <IdentityRow form={form} onChange={onChange} isCreate={isCreate} />
            <RolePayoutRow form={form} onChange={onChange} />
            <PermissionsBlock form={form} onChange={onChange} />
        </Stack>
    );
}

export default MemberFormFields;
